// Display helpers for Compare & Explore. Results are fractions of opening
// gross (-0.031 = −3.1%); dates are YYYY-MM-DD model dates.
const MINUS = '−';
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

// A real calendar date from year 1, as Python's date.fromisoformat reads it.
export function isIsoDate(value) {
    if (typeof value !== 'string' || !ISO_DATE.test(value) || value.startsWith('0000')) return false;
    const date = new Date(`${value}T00:00:00Z`);
    return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value;
}

// "+1.2%", "−3.4%"; anything that rounds to zero prints unsigned.
export function formatPercent(fraction, digits = 1) {
    if (!Number.isFinite(fraction)) return '—';
    const percent = fraction * 100;
    const text = Math.abs(percent).toFixed(digits);
    if (Number(text) === 0) return `${(0).toFixed(digits)}%`;
    return `${percent > 0 ? '+' : MINUS}${text}%`;
}

export function formatRange(fraction, digits = 1) {
    return Number.isFinite(fraction) ? `±${Math.abs(fraction * 100).toFixed(digits)}%` : '—';
}

export function formatUsd(value) {
    if (!Number.isFinite(value)) return '—';
    const text = Math.abs(value).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
    if (text === '$0') return text;
    return `${value > 0 ? '+' : MINUS}${text}`;
}

// "$1.25M", "$980K".
export function formatCompactUsd(value) {
    if (!Number.isFinite(value)) return '—';
    return value.toLocaleString('en-US', { style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 2 });
}

const parts = (iso) => {
    const [year, month, day] = iso.split('-').map(Number);
    return { year, month, day };
};

// "15 Sep 2008".
export function formatDay(iso) {
    if (!isIsoDate(iso)) return '—';
    const { year, month, day } = parts(iso);
    return `${day} ${MONTHS[month - 1]} ${year}`;
}

// "6–9 Aug 2007", "15 Sep – 20 Nov 2008", "2 Dec 2015 – 11 Feb 2016".
export function formatWindow(start, end) {
    if (!isIsoDate(start) || !isIsoDate(end)) return '—';
    if (start === end) return formatDay(start);
    const a = parts(start);
    const b = parts(end);
    if (a.year === b.year && a.month === b.month) return `${a.day}–${b.day} ${MONTHS[b.month - 1]} ${b.year}`;
    if (a.year === b.year) return `${a.day} ${MONTHS[a.month - 1]} – ${formatDay(end)}`;
    return `${formatDay(start)} – ${formatDay(end)}`;
}

// Date pickers work in local calendar dates; converting through UTC could
// move a chosen day across midnight.
export function parsePickerDate(iso) {
    if (!isIsoDate(iso)) return null;
    const { year, month, day } = parts(iso);
    return new Date(year, month - 1, day);
}

export function pickerToIso(value) {
    if (!(value instanceof Date) || Number.isNaN(value.getTime())) return null;
    return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`;
}

export const reasonsText = (reasons) => (reasons || [])
    .map((reason) => (typeof reason === 'string' ? reason : reason?.message || reason?.reason_code))
    .filter(Boolean)
    .join(' ');

// The warnings on a cell that still has a result: clipped dates, a missing
// range, or exposure whose factors had no return then.
export function cellNotes(cell) {
    if (!cell?.available) return '';
    const notes = [reasonsText(cell.reasons)];
    if (cell.coverage?.status === 'partial' && Number.isFinite(cell.coverage.uncovered_exposure_share)) {
        notes.push(`${Math.round(cell.coverage.uncovered_exposure_share * 100)}% of the exposure had no factor return then.`);
    }
    return notes.filter(Boolean).join(' ');
}
